import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import PublicHeader from "../../components/PublicHeader";
import SiteFooter from "../../components/SiteFooter";
import CoeditorGate from "../../components/CoeditorGate";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const IDENTITY_Q = "How do you and your partner/s identify? (any other identities you'd like included)";

export default function Story() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [identity, setIdentity] = useState("");
  const [identityOther, setIdentityOther] = useState("");
  const [pub, setPub] = useState(null);
  const [locked, setLocked] = useState(false);
  const [storyError, setStoryError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const ev = await api.get(`/events/${id}/summary`);
        const pubCode = ev.data.publication === "dwh" || ev.data.publication === "dwt" ? ev.data.publication : null;
        setPub(pubCode);
        setLocked(ev.data.editable === false);
        const cat = ev.data.story_category;
        if (!cat) {
          toast.error("No story type on this submission yet.");
          return;
        }
        const r = await api.get(`/events/${id}/story-questions?category=${cat}`);
        setCategory(cat);
        setQuestions(r.data.questions || []);
        // Prefill saved answers when resuming
        try {
          const draft = await api.get(`/events/${id}/draft`);
          const prev = {};
          (draft.data.story_responses || []).forEach((x) => {
            if (x.question === IDENTITY_Q) {
              const parts = (x.answer || "").split("\n\nAlso: ");
              setIdentity(parts[0] || "");
              setIdentityOther(parts[1] || "");
            } else {
              prev[x.question] = x.answer;
            }
          });
          setAnswers(prev);
        } catch { /* noop */ }
      } catch (err) {
        const message = formatApiError(err, "We could not load your story questions.");
        setStoryError(message);
        toast.error(message);
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (pub) document.documentElement.dataset.pub = pub;
    return () => { delete document.documentElement.dataset.pub; };
  }, [pub]);

  const saveAnswers = async () => {
    const baseAnswers = questions.map((q) => ({
      question: q,
      answer: (answers[q] || "").trim(),
    }));
    if (identity.trim()) {
      baseAnswers.unshift({
        question: IDENTITY_Q,
        answer: identity.trim() + (identityOther.trim() ? `\n\nAlso: ${identityOther.trim()}` : ""),
      });
    }
    await api.post(`/events/${id}/story`, { category, answers: baseAnswers });
  };

  const submit = async () => {
    const hasStory = !!identity.trim() || questions.some((q) => (answers[q] || "").trim());
    if (!hasStory) {
      const message = "Please answer at least one story question before continuing. You can leave the other questions blank.";
      setStoryError(message);
      toast.error(message);
      return;
    }
    setStoryError("");
    setSubmitting(true);
    try {
      await saveAnswers();
      navigate(`/create-event/${id}/submit`);
    } catch (err) {
      const message = formatApiError(err, "We could not save your story answers.");
      setStoryError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // "Skip story, for now" — save progress and leave; submit stays blocked
  // until the story has answers.
  const skip = async () => {
    try {
      await saveAnswers();
    } catch { /* progress save is best-effort here */ }
    navigate("/my-submissions");
  };

  if (loading) return <div className="min-h-screen bg-[#f5f3ed] flex items-center justify-center text-[#5c5852]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="story-page" data-pub={pub || "neutral"}>
      <PublicHeader pub={pub} />

      <CoeditorGate eventId={id}>
      <main className="w-full max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16">
        {locked && (
          <div className="bg-white border border-[#c19872] rounded-xl p-4 text-sm text-black mb-8" data-testid="locked-banner">
            This submission is with our editorial team and can no longer be edited.
          </div>
        )}

        <div className="mb-10">
          <p className="btw-step-label">Almost done</p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">Tell us your story</h1>
          <p className="text-sm text-black leading-relaxed max-w-xl">
            Answer at least one question to submit your story; you can leave the others
            blank. The more you share, the better we can tell it in a way that feels
            authentic to what you experienced.
          </p>
        </div>

        {category && (
          <div>
            <div className="btw-section" data-testid="story-identity">
              <p className="font-bold mb-1">How do you and your partner/s identify?</p>
              <p className="text-xs text-[#5c5852] mb-3">(Optional - e.g. queer woman, non-binary lesbian, gay lovers)</p>
              <textarea
                rows={2}
                aria-label="How do you and your partner/s identify?"
                data-testid="story-identity-input"
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
                placeholder="In your own words"
                className="w-full bg-transparent border-b border-[var(--field-line)] focus:border-black outline-none py-2 text-black resize-none"
              />
              <p className="font-bold mt-6 mb-2">Any other identities you'd like to include?</p>
              <input
                data-testid="story-identity-other"
                aria-label="Any other identities you'd like to include?"
                value={identityOther}
                onChange={(e) => setIdentityOther(e.target.value)}
                className="w-full bg-transparent border-b border-[var(--field-line)] focus:border-black outline-none py-2 text-sm text-black"
              />
            </div>

            <div className="space-y-6">
              {questions.map((q, i) => (
                <div key={i} className="btw-section !mb-0" data-testid={`story-q-${i}`}>
                  <p className="font-bold mb-3">{q}</p>
                  <textarea
                    rows={4}
                    aria-label={q}
                    data-testid={`story-a-${i}`}
                    value={answers[q] || ""}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q]: e.target.value }))}
                    className="w-full bg-transparent border-b border-[var(--field-line)] focus:border-black outline-none py-2 text-black resize-none"
                  />
                </div>
              ))}
            </div>

            {storyError && <p role="alert" className="text-sm text-[#b35c5c] mt-6" data-testid="story-submit-error">{storyError}</p>}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end justify-between gap-6 pt-10">
              <button
                type="button"
                onClick={skip}
                className="text-left"
                data-testid="story-skip-after"
              >
                <span className="block text-sm text-black underline underline-offset-4">Skip story, for now.</span>
                <span className="block text-xs text-[#5c5852] mt-1">We cannot finalise a submission without the story</span>
              </button>
              <button type="button" onClick={submit} disabled={submitting || locked} data-testid="story-submit" className="btw-btn w-full sm:w-auto">
                <span>{submitting ? "Sending…" : "Continue to Submit"}</span>
                <span className="btw-btn-arrow"><ArrowRight size={12} /></span>
              </button>
            </div>
          </div>
        )}
      </main>
      </CoeditorGate>

      <SiteFooter pub={pub} />
    </div>
  );
}

